import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Editable Plans page — caps + feature flags per tier, with a
 * "Force reconciler" maintenance panel at the bottom.
 *
 * Two parallel grids:
 *
 *   - Caps grid (numeric inputs). Each row is one cap; columns are
 *     the two tiers. -1 = unlimited; the "∞" toggle flips between
 *     bounded and unlimited without losing the previous numeric.
 *
 *   - Flags grid (checkboxes). Each row is one feature flag; columns
 *     are the two tiers. Tick = enabled.
 *
 * Edits stay client-side until you hit Save — at which point the
 * whole effective state for that tier is POSTed to the server. The
 * server upserts the override row and refreshes PlanCatalog so the
 * change takes effect instantly across the API surface.
 *
 * Read-only roles see the grids but the inputs are disabled and
 * Save/Reset are hidden.
 *
 * @author <lukman@zshift.io>
 */
export function Plans() {
  const { me } = useAdminAuth();
  const { toast, confirm } = useUI();

  const [meta, setMeta] = useState(null);       // { caps:[{key,label}], flags:[{key,label}] }
  const [tiers, setTiers] = useState([]);       // server's current state
  const [draft, setDraft] = useState(null);     // local edits (per-tier)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);   // tier currently saving, or null

  const canEdit = me?.role === 'SUPERADMIN';

  // ── Initial load ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api('/api/admin/plans'),
      api('/api/admin/plans/keys'),
    ])
      .then(([rows, k]) => {
        if (cancelled) return;
        setTiers(rows || []);
        setMeta(k || { caps: [], flags: [] });
        // Seed draft state from the server response.
        setDraft(asDraft(rows || []));
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e.message || 'Could not load plans.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Derived state — "is this tier dirty?" ─────────────────────
  const dirtyByTier = useMemo(() => {
    if (!draft) return {};
    const out = {};
    for (const t of tiers) {
      const server = { caps: t.caps || {}, flags: new Set(t.features || []) };
      const d = draft[t.id] || { caps: {}, flags: new Set() };
      let dirty = false;
      for (const k of Object.keys(server.caps)) {
        if ((d.caps[k] ?? null) !== server.caps[k]) { dirty = true; break; }
      }
      if (!dirty) {
        for (const k of Object.keys(d.caps)) {
          if (!(k in server.caps) || d.caps[k] !== server.caps[k]) { dirty = true; break; }
        }
      }
      if (!dirty) {
        if (d.flags.size !== server.flags.size) dirty = true;
        else for (const f of d.flags) { if (!server.flags.has(f)) { dirty = true; break; } }
      }
      out[t.id] = dirty;
    }
    return out;
  }, [draft, tiers]);

  // ── Mutators ──────────────────────────────────────────────────
  const setCap = (tier, key, value) => {
    setDraft((d) => ({
      ...d,
      [tier]: {
        ...(d[tier] || {}),
        caps: { ...(d[tier]?.caps || {}), [key]: value },
      },
    }));
  };
  const toggleFlag = (tier, flag) => {
    setDraft((d) => {
      const cur = d[tier]?.flags || new Set();
      const next = new Set(cur);
      if (next.has(flag)) next.delete(flag); else next.add(flag);
      return { ...d, [tier]: { ...(d[tier] || {}), flags: next } };
    });
  };

  // ── Save one tier ─────────────────────────────────────────────
  const save = async (tierId) => {
    if (!draft || !draft[tierId]) return;
    const body = {
      caps: draft[tierId].caps,
      flags: Array.from(draft[tierId].flags || []),
    };
    setSaving(tierId);
    try {
      const res = await api(`/api/admin/plans/${tierId}`, { method: 'PUT', body });
      // Refresh server state from response.
      setTiers((arr) => arr.map((t) => t.id === tierId
        ? { ...t, caps: res.caps, features: res.flags }
        : t
      ));
      // And re-sync draft to match (clears dirty flag).
      setDraft((d) => ({
        ...d,
        [tierId]: {
          caps: { ...res.caps },
          flags: new Set(res.flags || []),
        },
      }));
      toast.success(`${tierId} plan updated.`);
    } catch (e) {
      toast.error(e.message || 'Save failed.');
    } finally {
      setSaving(null);
    }
  };

  // ── Reset one tier to in-code defaults ───────────────────────
  const reset = async (tierId) => {
    const ok = await confirm({
      title: `Reset ${tierId} to defaults?`,
      message: 'Drops the override row for this tier. The in-code PlanCatalog defaults apply immediately. Cannot be undone.',
      confirmLabel: 'Reset',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await api(`/api/admin/plans/${tierId}`, { method: 'DELETE' });
      setTiers((arr) => arr.map((t) => t.id === tierId
        ? { ...t, caps: res.caps, features: res.flags }
        : t
      ));
      setDraft((d) => ({
        ...d,
        [tierId]: {
          caps: { ...res.caps },
          flags: new Set(res.flags || []),
        },
      }));
      toast.success(`${tierId} reset to defaults.`);
    } catch (e) {
      toast.error(e.message || 'Reset failed.');
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Plans &amp; <em>features</em></h1>
          <p className="page-sub">
            Caps + feature flags per tier. Saving writes a row to{' '}
            <code style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>plan_overrides</code>
            {' '}and reloads the catalog instantly — no restart needed.
          </p>
        </div>
      </div>

      {loading && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
          Loading…
        </div>
      )}

      {!loading && meta && (
        <>
          {/* Caps grid */}
          <div className="card" style={{ marginBottom: 22 }}>
            <div className="card-head">
              <h3>Caps</h3>
              <div className="sub">NUMERIC QUOTAS · −1 OR ∞ MEANS UNLIMITED</div>
            </div>
            <div className="table-wrap">
              <table className="t plan-grid">
                <thead>
                  <tr>
                    <th>Feature</th>
                    {tiers.map((t) => <th key={t.id} style={{ textAlign: 'center', minWidth: 180 }}>{t.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {meta.caps.map(({ key, label }) => (
                    <tr key={key}>
                      <th scope="row" style={{ textAlign: 'left', fontWeight: 500 }}>
                        {label}
                        <small style={{ display: 'block', color: 'var(--ink-3)', fontFamily: 'Geist Mono, monospace', fontSize: 10.5, fontWeight: 400 }}>
                          {key}
                        </small>
                      </th>
                      {tiers.map((t) => {
                        const value = draft?.[t.id]?.caps?.[key];
                        return (
                          <td key={t.id} style={{ textAlign: 'center' }}>
                            <CapInput
                              value={value ?? -1}
                              onChange={(v) => setCap(t.id, key, v)}
                              disabled={!canEdit}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Flags grid */}
          <div className="card" style={{ marginBottom: 22 }}>
            <div className="card-head">
              <h3>Feature flags</h3>
              <div className="sub">ON / OFF SWITCHES</div>
            </div>
            <div className="table-wrap">
              <table className="t plan-grid">
                <thead>
                  <tr>
                    <th>Feature</th>
                    {tiers.map((t) => <th key={t.id} style={{ textAlign: 'center', minWidth: 180 }}>{t.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {meta.flags.map(({ key, label }) => (
                    <tr key={key}>
                      <th scope="row" style={{ textAlign: 'left', fontWeight: 500 }}>
                        {label}
                        <small style={{ display: 'block', color: 'var(--ink-3)', fontFamily: 'Geist Mono, monospace', fontSize: 10.5, fontWeight: 400 }}>
                          {key}
                        </small>
                      </th>
                      {tiers.map((t) => {
                        const on = draft?.[t.id]?.flags?.has(key) ?? false;
                        return (
                          <td key={t.id} style={{ textAlign: 'center' }}>
                            <label className={`flag-cell ${on ? 'on' : ''}`}>
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={!canEdit}
                                onChange={() => toggleFlag(t.id, key)}
                              />
                              <span>{on ? 'On' : 'Off'}</span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-tier save row */}
          {canEdit && (
            <div className="row r-1-1" style={{ marginBottom: 22 }}>
              {tiers.map((t) => (
                <div className="card" key={t.id} style={{ padding: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <strong>{t.name}</strong>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {dirtyByTier[t.id] ? 'Pending changes' : 'No changes'}
                        {' · '}
                        {(t.users ?? 0).toLocaleString()} users on this tier
                      </div>
                    </div>
                    <div style={{ display: 'inline-flex', gap: 8 }}>
                      <button className="btn ghost" onClick={() => reset(t.id)} disabled={saving === t.id}>
                        Reset to defaults
                      </button>
                      <button
                        className="btn primary"
                        onClick={() => save(t.id)}
                        disabled={!dirtyByTier[t.id] || saving === t.id}
                      >
                        {saving === t.id ? 'Saving…' : 'Save ' + t.name}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!canEdit && (
            <div className="card" style={{ padding: 14, marginBottom: 22, fontSize: 13, color: 'var(--ink-3)' }}>
              Read-only view — only Superadmins can edit the catalog.
            </div>
          )}

          {/* Reconciler maintenance card (unchanged from before) */}
          <div className="row">
            <ReconcilerCard
              isSuperadmin={canEdit}
              onConfirm={confirm}
              onToast={toast}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ── Cap input — numeric field with "∞" toggle ───────────────────
function CapInput({ value, onChange, disabled }) {
  const isUnlimited = value === -1 || value == null;
  const [textValue, setTextValue] = useState(isUnlimited ? '' : String(value));

  // Re-sync local text when the source value changes from above
  // (e.g. after Save bounces back from the server).
  useEffect(() => {
    setTextValue(isUnlimited ? '' : String(value));
  }, [value, isUnlimited]);

  const setBounded = (raw) => {
    setTextValue(raw);
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '-') {
      onChange(0);
      return;
    }
    const n = Number(trimmed);
    if (Number.isFinite(n)) onChange(Math.max(0, Math.round(n)));
  };

  const toggleUnlimited = () => {
    if (isUnlimited) {
      onChange(0);
    } else {
      onChange(-1);
    }
  };

  return (
    <div className="cap-input">
      <input
        type="number"
        min="0"
        value={textValue}
        placeholder={isUnlimited ? '∞' : '0'}
        disabled={disabled || isUnlimited}
        onChange={(e) => setBounded(e.target.value)}
      />
      <button
        type="button"
        className={`cap-unlim ${isUnlimited ? 'on' : ''}`}
        onClick={toggleUnlimited}
        disabled={disabled}
        title={isUnlimited ? 'Set a numeric cap' : 'Make this cap unlimited'}
      >
        ∞
      </button>
    </div>
  );
}

// ── Maintenance card (same as before) ──────────────────────────
function ReconcilerCard({ isSuperadmin, onConfirm, onToast }) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const run = async () => {
    const ok = await onConfirm({
      title: 'Force-run the plan reconciler?',
      message: 'Walks every Premium user whose subscription_until has elapsed by more than 30 minutes and flips them to Basic. The reconciler also runs automatically at 03:30 UTC daily — this button is for known-webhook-outage recovery or post-deploy verification.',
      confirmLabel: 'Run reconciler',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api('/api/admin/plans/reconcile', { method: 'POST' });
      setLast({ ...res, at: new Date().toISOString() });
      onToast.success(res.message || 'Reconciler done.');
    } catch (e) {
      onToast.error(e.message || 'Reconciler failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Plan reconciler</h3>
          <div className="sub">SAFETY NET · RUNS DAILY @ 03:30 UTC</div>
        </div>
        <span className="pill success"><span className="dot"/> AUTOMATIC</span>
      </div>
      <div style={{ padding: '18px 22px 20px', display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280, fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)' }}>
          <p style={{ margin: '0 0 10px' }}>
            Every day at <strong style={{ fontFamily: 'Geist Mono, monospace' }}>03:30 UTC</strong>, the reconciler scans for
            Premium accounts whose <code style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>subscription_until</code> has
            elapsed and flips them back to Basic — gateway-agnostic
            (Stripe + RevenueCat both populate that column).
          </p>
          <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>
            Use the button if Stripe or RevenueCat had a webhook outage you want
            cleaned up immediately, or to verify a fresh deploy.
          </p>
          {last && (
            <div style={{ marginTop: 14, padding: '10px 12px', background: 'var(--bg-2)', borderRadius: 9, fontSize: 12.5 }}>
              <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Last manual run
              </div>
              <div>
                Checked <strong>{last.checked ?? 0}</strong> ·
                {' '}Downgraded <strong>{last.demoted ?? 0}</strong>
                {(last.failed > 0) && (<> · <span style={{ color: 'var(--red)' }}>Failed {last.failed}</span></>)}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          <button
            className="btn primary"
            onClick={run}
            disabled={busy || !isSuperadmin}
            title={isSuperadmin ? 'Run the reconciler now' : 'Superadmin role required'}
          >
            <Icon name="refresh" size={14} />
            {busy ? 'Reconciling…' : 'Force reconcile now'}
          </button>
          {!isSuperadmin && (
            <small style={{ fontSize: 11, color: 'var(--ink-3)' }}>Superadmin only</small>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────
// Take the server's tier list and turn it into our draft shape:
//   { BASIC: { caps: {key:value}, flags: Set<key> }, PREMIUM: {...} }
// Sets give O(1) toggle lookups + clean serialisation via Array.from.
function asDraft(rows) {
  const out = {};
  for (const t of rows) {
    out[t.id] = {
      caps: { ...(t.caps || {}) },
      flags: new Set(t.features || []),
    };
  }
  return out;
}
